import React, { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { URLs } from "../../utils/apis";
import { useStylesUserProfileAccountSettings } from "../../utils/mantine/useStylesUserProfileAccountSettings";
import { useStylesProfileSelect } from "../../utils/mantine/useStylesProfileSelect";
import { useStylesTooltip } from "../../utils/mantine/useStylesTooltip";
import CustomButton from "../mantineCustomComponents/CustomButton";
import CustomTooltip from "../mantineCustomComponents/CustomTooltip";

export default function StampNotificationSettings({ initialSettings }) {
  const [settings, setSettings] = useState(initialSettings);
  const [collections, setCollections] = useState([]);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collectionError, setCollectionError] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const selectId = useId();
  const detailsId = useId();
  const headingId = useId();
  const { classes } = useStylesUserProfileAccountSettings();
  const { classes: profileSelectClasses } = useStylesProfileSelect();
  const { classes: tooltipClasses } = useStylesTooltip();
  const fieldClasses = {
    root: classes.emailRow,
    label: classes.emailLabel,
    wrapper: classes.emailControl,
    description: classes.emailControl,
    error: classes.emailControl,
    input: classes.emailInput,
  };
  const rules = settings.rules || [];

  useEffect(() => {
    const controller = new AbortController();
    fetch(URLs.PROFILE_COLLECTIONS_URL, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Collections unavailable");
        return response.json();
      })
      .then((data) => setCollections(data.user_collections || []))
      .catch((err) => {
        if (err.name !== "AbortError") setCollectionError(true);
      });
    return () => controller.abort();
  }, []);

  const change = (update) => {
    setSettings((current) => ({ ...current, ...update }));
    setSaved(false);
  };
  const changeRule = (index, update) =>
    change({ rules: rules.map((rule, i) => (i === index ? update : rule)) });
  const choices = [
    { value: "all", label: "All collections and uncollected stamps" },
  ];
  const known = new Set(["all"]);
  for (const collection of collections) {
    if (collection.cid && !known.has(collection.cid.toLowerCase())) {
      known.add(collection.cid.toLowerCase());
      choices.push({
        value: collection.cid.toLowerCase(),
        label: collection.name || collection.cid,
      });
    }
  }
  for (const rule of rules) {
    if (rule.collection_cid && !known.has(rule.collection_cid)) {
      known.add(rule.collection_cid);
      choices.push({ value: rule.collection_cid, label: rule.collection_cid });
    }
  }

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    const payload = { ...settings };
    for (const field of ["recipient_to", "reply_bcc_to"]) {
      if (!payload[field]?.trim()) delete payload[field];
      else payload[field] = payload[field].trim();
    }
    try {
      const response = await fetch(URLs.USER_INFO_SAVE_PROFILE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken":
            document.querySelector("[name=csrfmiddlewaretoken]")?.value || "",
        },
        body: JSON.stringify({ stamp_notification_settings: payload }),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(
          typeof data.error === "string"
            ? data.error
            : "Could not save notification settings.",
        );
      setSettings(payload);
      setSaved(true);
    } catch (err) {
      setError(err.message || "Could not save notification settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack
      component="section"
      aria-labelledby={headingId}
      className={classes.emailSection}
      gap="md"
    >
      <Title id={headingId} order={2} className={classes.emailHeading}>
        Stamp Confirmation Emails
      </Title>
      <Box className={classes.emailMasterRow}>
        <Box className={classes.emailLabel}>
          <Group className={classes.emailLabelContent} gap={7.5} wrap="nowrap">
            <Text component="label" htmlFor={selectId} inherit>
              Send confirmation emails
            </Text>
            <CustomTooltip
              ariaLabel="About confirmation emails"
              content={
                <Text className={tooltipClasses.tooltipText}>
                  Receive confirmations for stamps created through the app, API,
                  and brokerage integrations. Select Yes to enable emails, then
                  choose recipients and manage collection-specific confirmations
                  below.
                </Text>
              }
            />
          </Group>
        </Box>
        <Stack className={classes.emailControl} gap={4}>
          <Select
            id={selectId}
            classNames={{ input: profileSelectClasses.input }}
            disabled={saving}
            data={[
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
            value={String(settings.send_email === true)}
            allowDeselect={false}
            onChange={(value) => change({ send_email: value === "true" })}
          />
        </Stack>
        <Box className={classes.emailSave}>
          <CustomButton
            text="Save"
            componentClasses="link"
            type="button"
            variant="transparent"
            loading={saving}
            onClick={save}
          />
        </Box>
      </Box>
      <Stack className={classes.emailFields} gap="md">
        <TextInput
          classNames={fieldClasses}
          inputWrapperOrder={["label", "input", "description", "error"]}
          disabled={saving}
          label="Send to"
          description="Separate addresses with commas. Leave blank to use your account email."
          type="email"
          multiple
          value={settings.recipient_to || ""}
          onChange={(event) =>
            change({ recipient_to: event.currentTarget.value })
          }
        />
        <TextInput
          classNames={fieldClasses}
          inputWrapperOrder={["label", "input", "description", "error"]}
          disabled={saving}
          label="Blind copy (BCC)"
          description="Optional. Separate addresses with commas. Applies to every confirmation."
          type="email"
          multiple
          value={settings.reply_bcc_to || ""}
          onChange={(event) =>
            change({ reply_bcc_to: event.currentTarget.value })
          }
        />
        <Anchor
          className={classes.emailDetailsToggle}
          component="button"
          type="button"
          size="sm"
          aria-expanded={detailsOpen}
          aria-controls={detailsId}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          Manage Collection-specific email confirmations
        </Anchor>
      </Stack>
      <div id={detailsId} className={classes.emailFields} hidden={!detailsOpen}>
        <Stack gap="md">
          <Box className={classes.emailRow}>
            <Text className={classes.emailControl} size="sm">
              Collection rules apply when confirmation emails are enabled. A
              specific collection rule overrides an all-collections rule.
            </Text>
          </Box>
          {collectionError && (
            <Alert color="yellow">
              Collections could not be loaded. Your existing settings can still
              be edited.
            </Alert>
          )}
          {rules.map((rule, index) => (
            <Stack key={index} gap="xs">
              <Select
                classNames={fieldClasses}
                disabled={saving}
                label={`Collection rule ${index + 1}`}
                data={choices}
                searchable
                value={rule.collection_cid || "all"}
                onChange={(cid) => {
                  const next = { ...rule };
                  if (!cid || cid === "all") delete next.collection_cid;
                  else next.collection_cid = cid;
                  changeRule(index, next);
                }}
              />
              <Select
                classNames={fieldClasses}
                disabled={saving}
                label={`Email setting ${index + 1}`}
                data={[
                  { value: "inherit", label: "Use global setting" },
                  { value: "true", label: "Send" },
                  { value: "false", label: "Do not send" },
                ]}
                value={
                  rule.send_email === undefined
                    ? "inherit"
                    : String(rule.send_email)
                }
                onChange={(value) => {
                  const next = { ...rule };
                  if (value === "inherit") delete next.send_email;
                  else next.send_email = value === "true";
                  changeRule(index, next);
                }}
              />
              <Group justify="flex-end">
                <Button
                  disabled={saving}
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    change({ rules: rules.filter((_, i) => i !== index) })
                  }
                >
                  Remove rule {index + 1}
                </Button>
              </Group>
            </Stack>
          ))}
        </Stack>
      </div>
      {detailsOpen && (
        <Box className={`${classes.emailActions} ${classes.emailFields}`}>
          <Button
            variant="light"
            disabled={saving || rules.length >= 100}
            onClick={() => change({ rules: [...rules, {}] })}
          >
            Add collection rule
          </Button>
        </Box>
      )}
      {error && (
        <Alert color="red" role="alert">
          {error}
        </Alert>
      )}
      {saved && (
        <Text c="green" role="status">
          Email settings saved.
        </Text>
      )}
    </Stack>
  );
}

StampNotificationSettings.propTypes = {
  initialSettings: PropTypes.object.isRequired,
};
